import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import {
  DEFAULT_THEME_CONFIG,
  ThemeMode,
  type ThemeBorder,
  type ThemeConfig,
  type ThemeDensity,
  type ThemePreset,
  THEME_PRESETS,
} from "@/shared/theme/lib/types";
import { applyTheme } from "@/shared/theme/lib/apply-theme";
import { loadThemeConfig, saveThemeConfig } from "@/shared/theme/lib/storage";

type ThemeContextValue = {
  config: ThemeConfig;
  setThemeConfig: (config: ThemeConfig) => void;
  setMode: (mode: ThemeMode) => void;
  setPrimary: (primary: string) => void;
  setAccent: (accent: string) => void;
  setRadius: (radius: number) => void;
  setDensity: (density: ThemeDensity) => void;
  setBorder: (border: ThemeBorder) => void;
  applyPreset: (preset: ThemePreset) => void;
  resetTheme: () => void;
};

const noop = () => {};

const ThemeContext = createContext<ThemeContextValue>({
  config: DEFAULT_THEME_CONFIG,
  setThemeConfig: noop,
  setMode: noop,
  setPrimary: noop,
  setAccent: noop,
  setRadius: noop,
  setDensity: noop,
  setBorder: noop,
  applyPreset: noop,
  resetTheme: noop,
});

type ThemePatch = Partial<ThemeConfig> | ((current: ThemeConfig) => Partial<ThemeConfig>);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<ThemeConfig>(() => loadThemeConfig());

  useEffect(() => {
    applyTheme(config);
    saveThemeConfig(config);
  }, [config]);

  useEffect(() => {
    if (config.mode !== ThemeMode.System || typeof window === "undefined") return;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyTheme(config);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [config]);

  const updateConfig = useCallback((patch: ThemePatch) => {
    setConfig((current) => {
      const nextPatch = typeof patch === "function" ? patch(current) : patch;
      return { ...current, ...nextPatch };
    });
  }, []);

  const setThemeConfig = useCallback((nextConfig: ThemeConfig) => {
    setConfig(nextConfig);
  }, []);
  const setMode = useCallback((mode: ThemeMode) => {
    updateConfig({ mode });
  }, [updateConfig]);
  const setPrimary = useCallback((primary: string) => {
    updateConfig({ primary });
  }, [updateConfig]);
  const setAccent = useCallback((accent: string) => {
    updateConfig({ accent });
  }, [updateConfig]);
  const setRadius = useCallback((radius: number) => {
    updateConfig({ radius });
  }, [updateConfig]);
  const setDensity = useCallback((density: ThemeDensity) => {
    updateConfig({ density });
  }, [updateConfig]);
  const setBorder = useCallback((border: ThemeBorder) => {
    updateConfig({ border });
  }, [updateConfig]);
  const applyPreset = useCallback((preset: ThemePreset) => {
    updateConfig({
      preset,
      primary: THEME_PRESETS[preset].primary,
      accent: THEME_PRESETS[preset].accent,
    });
  }, [updateConfig]);
  const resetTheme = useCallback(() => {
    setConfig(DEFAULT_THEME_CONFIG);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      config,
      setThemeConfig,
      setMode,
      setPrimary,
      setAccent,
      setRadius,
      setDensity,
      setBorder,
      applyPreset,
      resetTheme,
    }),
    [applyPreset, config, resetTheme, setAccent, setBorder, setDensity, setMode, setPrimary, setRadius, setThemeConfig],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useThemeContext() {
  return useContext(ThemeContext);
}
